import { useCallback, useState } from 'react';
import { listSittingsForVisit } from '../services/api/patientPlanApi';
import useSittingStore from '../stores/sittingStore';
import VisitCompletionPrompt from '../components/visit/VisitCompletionPrompt';
import { isFollowUpSet } from '../components/visit/FollowUpPicker';

// Req 6 — sittings in these states are done/closed; anything else on the visit
// is "open" and triggers the confirmation prompt.
const TERMINAL = new Set(['completed', 'paid', 'cancelled']);

// A procedure's payment is cleared once it's collected and the sitting has moved
// past the awaiting-payment gate (mirrors backend treatment_plans.py:1053).
const isPaid = (s) =>
  !!s?.payment_cleared_at && String(s?.status || '').toLowerCase() !== 'awaiting_payment';

/**
 * useVisitCompletionGuard — shared visit-completion gate (Req 6).
 *
 * Every screen that completes a visit (ManageVisitPage, DoctorQueueView, …)
 * routes through this so the behaviour is identical and the logic lives in ONE
 * place. Before completing, it checks for open (non-terminal, incl. delegated)
 * procedures on the visit; if any remain it shows `VisitCompletionPrompt`
 * instead of silently completing. The visit can still complete from the prompt
 * — it's a confirmation, not a hard block.
 *
 * @param {Function} updateVisit  (visitId, payload) => Promise — visit store action
 * @param {Object}   viewer       { memberId, role }
 * @param {Function} onCompleted  (visitId) => void — screen-specific post-complete
 *                                 work (workflow log, UI reset). Runs after the
 *                                 visit is actually marked COMPLETED, on BOTH the
 *                                 no-open-procedures path and every prompt resolution.
 * @returns {{ requestComplete: Function, completionPromptNode: React.ReactNode }}
 *   requestComplete(visit|visitId) — call at the actual-complete moment.
 *   completionPromptNode           — mount once in the screen's render tree.
 */
export default function useVisitCompletionGuard({ updateVisit, viewer, onCompleted }) {
  const cancelSitting = useSittingStore((s) => s.cancelSitting);
  const [prompt, setPrompt] = useState(null); // { visit, openSittings }

  const requestComplete = useCallback(async (visitOrId) => {
    const visit = (visitOrId && typeof visitOrId === 'object') ? visitOrId : { visit_id: visitOrId };
    const visitId = visit?.visit_id;
    if (!visitId) return;
    let open = [];
    try {
      const r = await listSittingsForVisit(visitId);
      // listSittingsForVisit → { success, data: { sittings: [...], count } }.
      const all = Array.isArray(r?.data?.sittings)
        ? r.data.sittings
        : (Array.isArray(r?.data) ? r.data : []);
      open = all.filter((s) => !s?.is_historical
        && !TERMINAL.has(String(s?.status || '').toLowerCase()));
    } catch { /* fall through to a plain complete so a transient error never blocks the desk */ }

    // Decision tree (Req 6, refined):
    //  - no open procedure                       → complete silently
    //  - any PAID open procedure                 → FULL popup (close-out decision always needed,
    //                                               follow-up flag ignored)
    //  - all open procedures UNPAID + follow-up set → complete silently (already deferred)
    //  - all open procedures UNPAID + no follow-up  → FOLLOW-UP-ONLY popup (just defer it)
    if (open.length) {
      if (open.some(isPaid)) {
        setPrompt({ visit, openSittings: open, variant: 'full' });
        return;
      }
      if (!isFollowUpSet(visit)) {
        setPrompt({ visit, openSittings: open, variant: 'followup-only' });
        return;
      }
    }
    await updateVisit(visitId, { status: 'COMPLETED' });
    onCompleted?.(visitId);
  }, [updateVisit, onCompleted]);

  const completionPromptNode = prompt ? (
    <VisitCompletionPrompt
      visit={prompt.visit}
      viewer={viewer}
      variant={prompt.variant}
      initialOpenSittings={prompt.openSittings}
      updateVisit={updateVisit}
      cancelSitting={cancelSitting}
      onClose={() => setPrompt(null)}
      onCompleted={(vid) => { setPrompt(null); onCompleted?.(vid); }}
    />
  ) : null;

  return { requestComplete, completionPromptNode };
}
